import React, { useState } from "react";
import PropTypes from "prop-types";
import { Formik, yupToFormErrors } from "formik";
import classnames from "classnames";
import * as Yup from "yup";
import TermsCheckbox from "components/sell/TermsCheckbox";
import Checkbox from "components/shared/Checkbox";
import Select from "components/shared/Select";
import LcFetch from "lib/shared/fetch";
import PasswordField from "components/payments/fields/PasswordField";
import TextField from "components/payments/fields/TextField";
import MaskedTextField from "components/payments/fields/MaskedTextField";
import LcField from "components/payments/fields/LcField";
import {
  buildSignupParams,
  logAccountCreated,
  replaceHeaderCopy
} from "components/payments/util";

const emptyValues = {
  birthday: "",
  country_code: "",
  email: "",
  email_marketing_optin: false,
  password: "",
  terms_checkbox: false
};

const TERMS_PATH = "/terms";
const PRIVACY_PATH = "/privacy";

const validationSchema = Yup.object().shape({
  birthday: Yup.string().min(
    8,
    "Please enter your birthday in the MM/DD/YYYY format"
  ),
  country_code: Yup.string().required("Please select your country"),
  email: Yup.string()
    .email("Email is invalid")
    .required("Email can't be blank"),
  password: Yup.string()
    .required("Password can't be blank")
    .min(8, "Password is too short (minimum is 8 characters)"),
  terms_checkbox: Yup.bool().required().oneOf([true], "Required")
});

export function FullUserForm(props) {
  const {
    countries,
    countryCode,
    isGdprCountry,
    dispatch,
    validateUserPath,
    subscriptionType,
    requests
  } = props;
  const [isGdprCompliant, setIsGprCompliant] = useState(isGdprCountry);
  const autoOptInEmailMarketing = countryCode && !isGdprCountry;
  const initialValues = {
    ...emptyValues,
    country_code: countryCode,
    email_marketing_optin: autoOptInEmailMarketing
  };

  const validateUserForm = async (values) => {
    // Doing a frontend validation before we hit the server to save a round-trip
    let yupFormErrors = {};
    try {
      await validationSchema.validate(values, { abortEarly: false });
    } catch (e) {
      yupFormErrors = yupToFormErrors(e);
    }

    // Validate against server requirements
    const result = await requests.validateUser(validateUserPath, values);
    const data = await result.json();

    if (!data.valid) {
      const serverErrors = data.errors.reduce(
        (acc, [k, v]) => ({ ...acc, [k]: v[0] }),
        {}
      );
      return { ...serverErrors, ...yupFormErrors };
    }
    return yupFormErrors;
  };

  const onSubmit = async (values) => {
    const result = await LcFetch(
      "/",
      buildSignupParams(values, subscriptionType)
    );
    const data = await result.json();
    logAccountCreated(data.user_analytics);
    replaceHeaderCopy();
    dispatch({
      clientSecret: data.stripe_client_secret,
      type: "payment_step"
    });
  };
  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      validate={validateUserForm}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} data-testid="full-user-form">
          <TextField
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            name="email"
            text="Email address"
            errorMessage={formik.errors.email}
          />
          <PasswordField
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
            name="password"
            errorMessage={formik.errors.password}
          />
          {!countryCode && (
            <LcField
              text="Country"
              className="country"
              name="user_country_code"
            >
              <Select
                baseClassName="lc-select-container--md"
                name="country_code"
                value={formik.values.country_code}
                id="user_country_code"
                placeholder="Select one"
                options={countries}
                errorMessage={formik.errors.country_code}
                onChange={(e) => {
                  setIsGprCompliant(e.conforms_to_gdpr);
                  formik.setFieldValue("country_code", e.value);
                }}
                onBlur={formik.handleBlur}
              />
            </LcField>
          )}
          <MaskedTextField
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.birthday}
            name="birthday"
            placeholder="MM / DD / YYYY"
            text="Birthday"
            errorMessage={formik.errors.birthday}
          />
          <div
            className={classnames("optin-checkbox terms", {
              error: formik.errors.terms_checkbox
            })}
          >
            <TermsCheckbox
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.terms_checkbox}
              name="terms_checkbox"
              url={TERMS_PATH}
              privacyPolicyUrl={PRIVACY_PATH}
            />
          </div>
          {isGdprCompliant && (
            <Checkbox
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email_marketing_optin}
              name="email_marketing_optin"
              label="Get email updates when we publish new guides and add new features. You can unsubscribe at any time. (optional)" // eslint-disable-line max-len
              checked={formik.values.email_marketing_optin}
              containerClass="user_marketing_optin--full-form"
            />
          )}
          {subscriptionType && (
            <input type="hidden" name="subscription" value={subscriptionType} />
          )}
          <button
            type="submit"
            className="form-button form-submit btn btn-a-plus btn-block"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? "Submitting..." : "Continue to Payment"}
          </button>
        </form>
      )}
    </Formik>
  );
}

FullUserForm.propTypes = {
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      conforms_to_gdpr: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  countryCode: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  isGdprCountry: PropTypes.bool,
  subscriptionType: PropTypes.string,
  validateUserPath: PropTypes.string.isRequired
};

FullUserForm.defaultProps = {
  countryCode: "",
  subscriptionType: null
};

const requests = {
  createUser: (values) => LcFetch("/", buildSignupParams(values)),
  validateUser: (validateUserPath, values) =>
    LcFetch(validateUserPath, buildSignupParams(values))
};

export default function (props) {
  return <FullUserForm {...props} requests={requests} />;
}
